/**
 * @typedef PaletteNode
 * @type {Object}
 * @property {string} type - The unique identifier for the node.
 * @property {string} name - The label for the node.
 * @property {string} subtype - The subtype of the node.
 * @property {string} description - The description for the node.
 * @property {string} icon - The icon for the node - icon name from https://fonts.google.com/icons.
 * @property {Array.<string>} available_connections - The possible connected node type for the node.
 * */

/**
 * @typedef PaletteSection
 * @type {Object}
 * @property {string} id - The unique identifier for the section.
 * @property {string} label - The label for the section.
 * @property {PaletteNode[]} items - The items in the section.
 * */

/**
 * An array of palette nodes used in the application.
 * Categorized list divided into sections - each section contains nodes used in the journey builder.
 * Each section should have unique id.
 * @type PaletteSection[]
 */
export const PaletteNodes = [
  {
    id: 'startEndNodes',
    label: 'Start/End Nodes',
    items: [
      {
        type: 'start',
        subtype: 'api_trigger',
        item_type: 'start/end_nodes',
        name: 'Trigger Start',
        description: 'Starts a journey when an external API event occurs.',
        icon: 'settings_input_antenna', // material icon name
        available_connections: ['channel_nodes', 'decision_nodes', 'wait_nodes'],
        connection_limits: {
          "incoming": 0,
          "outgoing": 1
        }
      },
      {
        type: 'start',
        subtype: 'scheduled_start',
        item_type: 'start/end_nodes',
        name: 'Scheduled Start',
        description: 'Starts the journey at a predefined date and time.',
        icon: 'schedule_send',
        available_connections: ['channel_nodes', 'decision_nodes', 'wait_nodes'],
        connection_limits: {
          "incoming": 0,
          "outgoing": 1
        }
      },
      {
        name: 'End',
        type: 'end',
        subtype: 'stop',
        item_type: 'start/end_nodes',
        description: 'End node',
        icon: 'close',
        available_connections: [],
        connection_limits: {
          incoming: -1,
          outgoing: 0
        }
      }
    ]
  },
  {
    id: 'channelNodes',
    label: 'Channel Nodes',
    items: [
      {
        type: 'email',
        subtype: 'email',
        item_type: 'channel_nodes',
        name: 'Email',
        description: 'Sends an email to the subscriber.',
        icon: 'email',
        available_connections: ['channel_nodes', 'decision_nodes', 'wait_nodes', 'stop'],
        connection_limits: {
          incoming: 1,
          outgoing: 1
        }
      },
      {
        type: 'whatsapp',
        subtype: 'whatsapp',
        item_type: 'channel_nodes',
        name: 'Whatsapp',
        description: 'Sends a whatsapp message to the subscriber.',
        icon: '/images/jb/whatsapp.svg',
        available_connections: ['channel_nodes', 'decision_nodes', 'wait_nodes', 'stop'],
        connection_limits: {
          incoming: 1,
          outgoing: 1
        }
      },
      {
        type: 'sms',
        subtype: 'sms',
        item_type: 'channel_nodes',
        name: 'SMS',
        description: 'Sends an SMS notification.',
        icon: 'sms',
        available_connections: ['channel_nodes', 'decision_nodes', 'wait_nodes', 'stop'],
        connection_limits: {
          incoming: 1,
          outgoing: 1
        }
      },
      {
        type: 'rcs',
        subtype: 'rcs',
        item_type: 'channel_nodes',
        name: 'RCS',
        description: 'Sends a Rich Communication Service (RCS) message with rich media and interactive features.',
        icon: 'mobile_chat',
        available_connections: ['channel_nodes', 'decision_nodes', 'wait_nodes', 'stop'],
        connection_limits: {
          incoming: 1,
          outgoing: 1
        }
      }
    ]
  },
  {
    id: 'decisionNodes',
    label: 'Decision Nodes',
    items: [
      {
        type: 'case',
        subtype: 'switch',
        item_type: 'decision_nodes',
        name: 'Case',
        description: 'Splits the journey based on a condition.',
        icon: 'network_node',
        available_connections: ['channel_nodes', 'decision_nodes', 'wait_nodes', 'stop'],
        connection_limits: {
          incoming: 1,
          outgoing: -1
        }
      },
      {
        type: 'if-else',
        subtype: 'if-else',
        item_type: 'decision_nodes',
        name: 'If/Else',
        description: 'Adds an If/Else condition in the journey flow.',
        icon: 'alt_route',
        available_connections: ['channel_nodes', 'decision_nodes', 'wait_nodes', 'stop'],
        connection_limits: {
          incoming: 1,
          outgoing: -1
        }
      }
    ]
  },
  {
    id: 'waitNodes',
    label: 'Wait Nodes',
    items: [
      {
        type: 'wait_until',
        subtype: 'delay',
        item_type: 'wait_nodes',
        name: 'Wait Until',
        description: 'Pauses the journey until a specified condition is met.',
        icon: 'hourglass_pause',
        available_connections: ['channel_nodes', 'decision_nodes', 'wait_nodes', 'stop'],
        connection_limits: {
          incoming: 1,
          outgoing: 1
        }
      }, {
        type: "wait",
        subtype: "wait_timer",
        item_type: "wait_nodes",
        name: "Wait",
        description: "Add a time delay before proceeding to the next node with configurable absolute or relative timing.",
        icon: "lock_clock",
        available_connections: ["channel_nodes", "decision_nodes", "wait_nodes", "stop"],
        connection_limits: {
          incoming: 1,
          outgoing: 1
        },
      }
    ]
  }
]

export const PaletteStruct = [
  {
    id: 'startEndNodes',
    label: 'Start/End Nodes',
    type: 'start/end_nodes',
    items: []
  },
  {
    id: 'channelNodes',
    label: 'Channel Nodes',
    type: 'channel_nodes',
    items: []
  },
  {
    id: 'decisionNodes',
    label: 'Decision Nodes',
    type: 'decision_nodes',
    items: []
  },
  {
    id: 'waitNodes',
    label: 'Wait Nodes',
    type: 'wait_nodes',
    items: []
  }
]

export const nodeSubtypeListMap = {
  'start/end_nodes': ['scheduled_start', 'api_trigger', 'stop'],
  'channel_nodes': ['email', 'whatsapp', 'sms', 'rcs'],
  'decision_nodes': ['switch', 'if-else'],
  'wait_nodes': ['delay', 'wait_timer']
}

/**
 * Generates a template for a "Case" node in the workflow.
 * The template includes a label, a list of cases with conditions, and a default target.
 * Each case contains:
 * - `condition`: A string representing the condition for the case.
 * - `target`: The ID of the target node (initially empty).
 * - `edge_uid`: A unique identifier for the edge connecting the case to its target (initially empty).
 *
 * The default target is also included but is initially empty.
 *
 * @returns {Object} The template object for a "Case" node.
 */
export const getCaseNodeDataTemplate = () => ({
  label: 'Case',
  cases: [],
  default_target: ''
})
